/**
 * What an order's payment status means, said the same way on every screen.
 *
 * Cash on delivery is the case that went wrong: an unpaid COD order was shown
 * as "Pay on Delivery" in green on the storefront and "Cash on delivery" to the
 * seller, which read as settled. COD money moves in three steps, and each
 * audience cares about a different one:
 *
 *   COD_PENDING    nobody has paid yet — the customer pays at the door
 *   COD_COLLECTED  the courier took the cash — the customer has paid, the
 *                  platform has not received it yet
 *   CAPTURED       the courier remitted it — the platform holds the money
 */
export type PaymentAudience = 'customer' | 'seller' | 'admin';

export type PaymentKey =
  | 'PAID'
  | 'COD_DUE'
  | 'COD_COLLECTED'
  | 'PENDING'
  | 'FAILED'
  | 'REFUNDED'
  | 'NOT_CHARGED'
  | 'NONE';

export type PaymentTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface PaymentRowLike {
  provider: string;
  method?: string | null;
  status: string;
}

export interface PaymentView {
  key: PaymentKey;
  /** Short badge text. */
  label: string;
  /** One sentence of what happens next, or null. */
  detail: string | null;
  tone: PaymentTone;
  /** Settled from this audience's point of view. */
  isPaid: boolean;
  isCod: boolean;
}

const view = (
  key: PaymentKey,
  label: string,
  tone: PaymentTone,
  isPaid: boolean,
  isCod: boolean,
  detail: string | null = null,
): PaymentView => ({ key, label, detail, tone, isPaid, isCod });

export function describePayment(
  payments: readonly PaymentRowLike[] | null | undefined,
  audience: PaymentAudience,
  opts: { orderStatus?: string | null } = {},
): PaymentView {
  const rows = payments ?? [];
  const cod = rows.find((p) => p.provider === 'COD' || p.method === 'COD');
  const isCod = !!cod;
  const cancelled = opts.orderStatus === 'CANCELLED';

  if (rows.length === 0) return view('NONE', 'No payment', 'neutral', false, false);

  if (rows.some((p) => p.status === 'REFUNDED' || p.status === 'PARTIALLY_REFUNDED')) {
    return view('REFUNDED', 'Refunded', 'info', false, isCod);
  }

  if (cod) {
    if (cod.status === 'CAPTURED') {
      return audience === 'customer'
        ? view('PAID', 'Paid · Cash on Delivery', 'success', true, true)
        : view('PAID', 'Paid · COD remitted', 'success', true, true);
    }
    if (cod.status === 'COD_COLLECTED') {
      if (audience === 'customer') {
        return view('PAID', 'Paid · Cash on Delivery', 'success', true, true);
      }
      return audience === 'seller'
        ? view(
            'COD_COLLECTED',
            'COD collected · settlement pending',
            'info',
            false,
            true,
            'The courier collected the cash. It is paid out to you once they remit it.',
          )
        : view(
            'COD_COLLECTED',
            'Cash with courier · remittance pending',
            'warning',
            false,
            true,
            'Collected at delivery. Mark it remitted when the courier pays it in.',
          );
    }
    if (cod.status === 'COD_PENDING') {
      if (cancelled) return view('NOT_CHARGED', 'Not charged (COD)', 'neutral', false, true);
      if (audience === 'customer') {
        return view(
          'COD_DUE',
          'Payment pending · Pay on delivery',
          'warning',
          false,
          true,
          'Pay in cash or UPI to the delivery partner when your order arrives.',
        );
      }
      return audience === 'seller'
        ? view(
            'COD_DUE',
            'Payment pending · COD',
            'warning',
            false,
            true,
            'The customer pays the courier at delivery.',
          )
        : view(
            'COD_DUE',
            'Unpaid · COD due at delivery',
            'warning',
            false,
            true,
            'Nothing collected yet — the courier collects at delivery.',
          );
    }
  }

  const captured = rows.find((p) => p.status === 'CAPTURED');
  if (captured) {
    const via = captured.provider === 'RAZORPAY' ? 'Online' : titleCase(captured.provider);
    return view('PAID', `Paid · ${via}`, 'success', true, isCod);
  }

  if (cancelled) return view('NOT_CHARGED', 'Not charged', 'neutral', false, isCod);
  if (rows.some((p) => p.status === 'FAILED') && !rows.some((p) => p.status === 'CREATED')) {
    return view('FAILED', 'Payment failed', 'danger', false, isCod);
  }
  return view('PENDING', 'Payment pending', 'warning', false, isCod);
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
